/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.11.0
 */

import type { Dispatch, FC, SetStateAction } from 'react';
import { memo, useMemo, useRef } from 'react';

import { FixedSizeList } from 'react-window';

import { usePreventWindowScroll } from '@modules/app/hooks/usePreventWindowScroll';

import InfinityScrollWrapper from '../../InfinityScrollWrapper/InfinityScrollWrapper';
import { FoundItemMemo } from './FoundItem';
import type { IInputSearchItem, TInputSearchVirtual } from './types';

export interface IFoundItemsVirtualListProps {
  foundItems: IInputSearchItem[];
  virtual: TInputSearchVirtual;

  setOpen: Dispatch<SetStateAction<boolean>>;
  onSelect: (item: IInputSearchItem) => void;
}

export const FoundItemsVirtualList: FC<IFoundItemsVirtualListProps> = ({
  foundItems,
  virtual,
  setOpen,
  onSelect,
}) => {
  const listBodyRef = useRef<HTMLDivElement>(null);
  usePreventWindowScroll(listBodyRef);

  const { hasMore, isLoading, onScrollEvent } = virtual;

  const itemsCount = foundItems.length;
  const height = useMemo(
    () => (itemsCount * 32 > 200 ? 200 : itemsCount * 32),
    [itemsCount],
  );

  return (
    <InfinityScrollWrapper
      hasMore={hasMore}
      isLoading={isLoading}
      sizeElement={listBodyRef}
      watchElement={listBodyRef}
      onScrollEvent={onScrollEvent}
    >
      <FixedSizeList
        height={height}
        itemCount={itemsCount}
        itemSize={32}
        outerRef={listBodyRef}
        width="100%"
      >
        {({ index, style }) => {
          const currentItem = foundItems[index];

          return (
            currentItem && (
              <FoundItemMemo
                item={currentItem}
                setOpen={setOpen}
                style={style}
                onSelect={onSelect}
              />
            )
          );
        }}
      </FixedSizeList>
    </InfinityScrollWrapper>
  );
};

export const FoundItemsVirtualListMemo = memo(FoundItemsVirtualList);
